import React, { useEffect, useRef, useState } from "react";
import { Bar, BarChart, Cell, Tooltip, XAxis, YAxis } from "recharts";
import { TrendingDown, TrendingUp, Trophy } from "lucide-react";
import { api } from "../lib/api";

const NAVY = "#BFDCDA";
const NAVY_HOVER = "#7FB8B5";
const GREEN = "#0F6B6B";

const CHART_MARGIN = { top: 10, right: 8, left: -16, bottom: 0 };
const X_TICK_STYLE = { fill: "#6B635C", fontSize: 11, fontWeight: 600, letterSpacing: 1 };
const Y_TICK_STYLE = { fill: "#6B635C", fontSize: 10 };
const TOOLTIP_CURSOR = { fill: "rgba(15,107,107,0.06)" };
const BAR_RADIUS = [6, 6, 0, 0];
const REVENUE_PARAMS = { months: 6 };
const yTickFormatter = (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v);

const fmtAud = (n) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(n || 0);

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-[#E8E1D6] bg-[#FAF7F2] px-3 py-2 shadow-lg" data-testid="revenue-tooltip">
      <div className="text-[10px] uppercase tracking-widest text-[#6B635C]">{label}</div>
      <div className="text-sm font-bold text-[#0F6B6B]">{fmtAud(payload[0].value)}</div>
    </div>
  );
}

export default function RevenueChart() {
  const [data, setData] = useState(null);
  const [boxSize, setBoxSize] = useState({ w: 0, h: 0 });
  const boxRef = useRef(null);

  useEffect(() => {
    // Only render the Recharts ResponsiveContainer once the parent box has
    // measured non-zero dimensions. This eliminates the "width(-1) height(-1)"
    // warning that fires when ResponsiveContainer mounts before layout settles.
    if (!boxRef.current || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect && rect.width > 0 && rect.height > 0) {
        setBoxSize({ w: rect.width, h: rect.height });
      }
    });
    ro.observe(boxRef.current);
    return () => ro.disconnect();
  }, [data]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: res } = await api.get("/dashboard/revenue", { params: REVENUE_PARAMS });
        if (mounted) setData(res);
      } catch {
        if (mounted) setData({ series: [], best_month: null, mom_growth: null, current_total: 0 });
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (!data) {
    return (
      <div className="tm-card p-6" data-testid="revenue-chart-loading">
        <p className="text-[#6B635C] text-sm">Loading revenue…</p>
      </div>
    );
  }

  const { series, best_month: best, mom_growth: mom } = data;
  const maxIdx = series.reduce((acc, r, i) => (r.total > (series[acc]?.total ?? -1) ? i : acc), 0);
  const allZero = series.every((s) => s.total === 0);
  const momPositive = (mom ?? 0) >= 0;

  return (
    <section data-testid="revenue-chart-section">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="tm-label">Revenue · Paid Invoices</p>
          <h2 className="font-display text-2xl font-bold uppercase mt-1">Last 6 months</h2>
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="revenue-meta">
          {best && best.total > 0 && (
            <div
              className="flex items-center gap-2 rounded-md border border-[#E8E1D6] bg-[#FFFFFF] px-3 py-1.5"
              data-testid="revenue-best-month"
            >
              <Trophy className="w-3.5 h-3.5 text-[#0F6B6B]" />
              <span className="text-[10px] uppercase tracking-widest text-[#6B635C]">Best</span>
              <span className="text-sm font-bold text-[#2A2623]">{best.label}</span>
              <span className="text-xs text-[#0F6B6B]">{fmtAud(best.total)}</span>
            </div>
          )}
          {mom !== null && !allZero && (
            <div
              className="flex items-center gap-2 rounded-md border border-[#E8E1D6] bg-[#FFFFFF] px-3 py-1.5"
              data-testid="revenue-mom"
            >
              {momPositive ? (
                <TrendingUp className="w-3.5 h-3.5 text-[#0F6B6B]" />
              ) : (
                <TrendingDown className="w-3.5 h-3.5 text-[#A8372A]" />
              )}
              <span className="text-[10px] uppercase tracking-widest text-[#6B635C]">MoM</span>
              <span
                className={`text-sm font-bold ${momPositive ? "text-[#0F6B6B]" : "text-[#A8372A]"}`}
                data-testid="revenue-mom-value"
              >
                {momPositive ? "+" : ""}
                {mom}%
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="tm-card p-4 sm:p-6" data-testid="revenue-chart-card">
        {allZero ? (
          <div className="h-56 flex flex-col items-center justify-center text-center" data-testid="revenue-empty">
            <p className="text-[#2A2623] font-medium">No paid invoices yet.</p>
            <p className="text-[#6B635C] text-sm mt-1">Mark an invoice as paid to see your revenue trend.</p>
          </div>
        ) : (
          <div className="h-56 min-h-[224px] min-w-[280px]" data-testid="revenue-chart-canvas" ref={boxRef}>
            {boxSize.w > 0 && boxSize.h > 0 && (
              <BarChart
                width={boxSize.w}
                height={boxSize.h}
                data={series}
                margin={CHART_MARGIN}
              >
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  tick={X_TICK_STYLE}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={Y_TICK_STYLE}
                  tickFormatter={yTickFormatter}
                  width={48}
                />
                <Tooltip cursor={TOOLTIP_CURSOR} content={<ChartTooltip />} />
                <Bar dataKey="total" radius={BAR_RADIUS} maxBarSize={56}>
                  {series.map((entry, i) => (
                    <Cell key={entry.label} fill={i === maxIdx ? GREEN : NAVY} stroke={i === maxIdx ? GREEN : NAVY_HOVER} />
                  ))}
                </Bar>
              </BarChart>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
